import { Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Breakpoint, BreakpointSize } from '../layout.interface';
import { BreakpointL, BreakpointM, BreakpointS, BreakpointXL } from '../layout.const';
import { map, Observable, pluck, shareReplay } from 'rxjs';
import { BreakpointState } from '@angular/cdk/layout';
import { filterEmpty } from '@skyeng/libs/shared/rxjs';

@Injectable({
  providedIn: 'root',
})
export class DsBreakpointService {
  private mediaQueryS = this.getMediaQuery(BreakpointS);
  private mediaQueryM = this.getMediaQuery(BreakpointM);
  private mediaQueryL = this.getMediaQuery(BreakpointL);
  private mediaQueryXL = this.getMediaQuery(BreakpointXL);
  private allQueriesMap = {
    [this.mediaQueryS]: BreakpointSize.small,
    [this.mediaQueryM]: BreakpointSize.medium,
    [this.mediaQueryL]: BreakpointSize.large,
    [this.mediaQueryXL]: BreakpointSize.extraLarge,
  };

  private browserBreakpointListener = this.getBreakpointListener(Object.keys(this.allQueriesMap)).pipe(
    pluck('breakpoints'),
    map(breakpoints => Object.entries(breakpoints).find(([_, isMatched]) => isMatched)),
    filterEmpty(),
    map(([query, _]) => this.allQueriesMap[query]),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  constructor(private breakpointObserver: BreakpointObserver) {}

  isSize(breakpoint: Breakpoint): boolean {
    return this.breakpointObserver.isMatched(this.getMediaQuery(breakpoint));
  }

  isSmall(): boolean {
    return this.breakpointObserver.isMatched(this.mediaQueryS);
  }

  isMedium(): boolean {
    return this.breakpointObserver.isMatched(this.mediaQueryM);
  }

  isLarge(): boolean {
    return this.breakpointObserver.isMatched(this.mediaQueryL);
  }

  isExtraLarge(): boolean {
    return this.breakpointObserver.isMatched(this.mediaQueryXL);
  }

  getBreakpointListener(mediaQueries: string[]): Observable<BreakpointState> {
    return this.breakpointObserver.observe(mediaQueries);
  }

  getBreakpointLetterListener(): Observable<BreakpointSize> {
    return this.browserBreakpointListener;
  }

  private getMediaQuery(breakpoint: Breakpoint): string {
    const minWidthQuery = breakpoint.minWidth ? `(min-width: ${breakpoint.minWidth}px)` : null;
    const maxWidthQuery = breakpoint.maxWidth ? `(max-width: ${breakpoint.maxWidth}px)` : null;
    return [minWidthQuery, maxWidthQuery].filter(Boolean).join(' and ');
  }
}
